<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getText,
        getUserFilterType,
        goBack,
        goToURL,
        replaceURL,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { isMobile, user } from "../../stores";

    import {
        Button,
        NotificationModal,
        PageLoading,
        SearchInput,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import { deleteIcon, editIcon } from "../../Utils/utilConstants.svelte";

    import { cacheDefaultFilters } from "../../Utils/utilCache.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;

    $: isUserManager = $user.role == DirectUserRoles.Manager;

    let isLoadingFiltersData = false,
        filtersData = [];

    let filters = {};

    let page = 0,
        pageSize = 25,
        count = 0;

    let showDeleteDefaultFiltersModal = false,
        isDeletingDefaultFilters = false;

    let selectedFilterId;

    $: columns = [
        {
            key: "name",
            title: "Name",
            headerClass: `rounded-tl-xl text-left pl-3 ${isUserAdmin ? "w-[35%]" : "w-[45%]"}`,
            class: "text-left pl-3",
            value: (v) => getText(v.name),
        },
        {
            key: "filterType",
            title: "Type",
            headerClass: `text-left pl-3 ${isUserAdmin ? "w-[35%]" : "w-[45%]"}`,
            class: "text-left pl-3",
            value: (v) => getUserFilterType(v.filterType),
        },
        {
            key: "groupName",
            title: "Group",
            headerClass: "text-left pl-3 w-[20%]",
            class: "text-left pl-3",
            isHidden: !isUserAdmin,
            value: (v) => getText(v.groupName),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "rounded-tr-xl w-[2%] text-left pl-3",
            class: "text-left pl-3",
            renderValue: (v) => _renderTableButtons(),
            csvExportHidden: true,
        },
    ];

    onMount(async () => {
        if (!isUserAdmin && !isUserManager) goBack();
        else {
            if (params.query) updateSearchParamsByQuery(params.query);

            await loadFiltersData(
                page * pageSize,
                pageSize,
                _getSearchParams(),
            );
        }
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                const [key, value] = arr[i].split("=");

                if (key == "page") page = Number(value);

                if (key == "size") pageSize = Number(value);

                if (key == "search") filters.search = value;
            }

            updateURL();
        }
    };

    const loadFiltersData = async (start, limit, params) => {
        isLoadingFiltersData = true;

        await apiMethods.getMethod
            .getDefaultFilters(start, limit, params)
            .then((res) => _handleFiltersData(res));

        isLoadingFiltersData = false;
    };

    const createDefaultFilters = () => {
        goToURL("/DefaultFilters/create/new");
    };

    const editDefaultFilters = (id) => {
        goToURL(`/DefaultFilters/view/${id}`);
    };

    const deleteDefaultFilters = async () => {
        isDeletingDefaultFilters = true;

        await apiMethods.deleteMethod
            .deleteDefaultFilters(selectedFilterId)
            .then((res) => _handleDeleteDefaultFilters(res));

        isDeletingDefaultFilters = false;
    };

    const handleClickCell = (event, rowData, key) => {
        const { classList } = event.target;

        if (key == "buttons") {
            if (classList.contains("editIcon")) editDefaultFilters(rowData.id);
            else if (classList.contains("deleteIcon")) {
                selectedFilterId = rowData.id;
                _openDeleteDefaultFiltersModal();
            }
        }
    };

    const handleFilterSelect = () => {
        page = 0;
        filtersData = [];

        updateURL();

        loadFiltersData(0, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/DefaultFilters/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadFiltersData(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadFiltersData(0, newPageSize, _getSearchParams());
    };

    // HELPER FUNCTIONS
    const _renderTableButtons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2 mr-6">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit filter">
                        <i class="editIcon ${editIcon}"></i>
                    </div>
                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete filter">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        return params;
    };

    const _getParamsString = () => {
        let str = "";

        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _handleFiltersData = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load default filters data, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            filtersData = res.results;
            count = res.total;
        }
    };

    const _handleDeleteDefaultFilters = async (res) => {
        _closeDeleteDefaultFiltersModal();

        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to delete default filters, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            await loadFiltersData(
                page * pageSize,
                pageSize,
                _getSearchParams(),
            );

            await cacheDefaultFilters();
        }
    };

    const _openDeleteDefaultFiltersModal = () =>
        (showDeleteDefaultFiltersModal = true);

    const _closeDeleteDefaultFiltersModal = () => {
        selectedFilterId = null;
        showDeleteDefaultFiltersModal = false;
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div
        class="flex flex-0 {$isMobile
            ? 'flex-col gap-y-4 items-start'
            : 'justify-between items-end'}"
    >
        <div class="flex gap-4 flex-wra">
            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
            />
        </div>

        <Button clickFunc={createDefaultFilters}>Create Filter</Button>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoadingFiltersData}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={filtersData}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={filtersData}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: filtersData,
            fileName: "Pre-defined Filters",
            allDataApi: async () =>
                await apiMethods.getMethod.getDefaultFilters(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

{#if showDeleteDefaultFiltersModal}
    <NotificationModal
        title="Warning"
        isModalOpen={showDeleteDefaultFiltersModal}
        isReverseBtns
        cancelText="Delete"
        submitText="Cancel"
        confirmButtonClass="btn-accent"
        closeModal={deleteDefaultFilters}
        confirmAction={_closeDeleteDefaultFiltersModal}
        closeModalFunction={_closeDeleteDefaultFiltersModal}
    >
        Delete this filter?
    </NotificationModal>
{/if}
